import { bianka } from '@fiverr-private/obs';
import { getContext } from '@fiverr-private/fiverr_context';
import { ClickType, Click, Impression, ElementType } from '@fiverr-private/bianka_js';
import { ClickedBriefDrawer } from '@fiverr-private/bianka_js/protobuf/bianka/event/activity/v2/extension/dynamic_matches/clicked_brief_drawer';
import { ViewedBriefDrawer } from '@fiverr-private/bianka_js/protobuf/bianka/event/activity/v2/extension/dynamic_matches/viewed_brief_preview_drawer';
import { UserMode } from '@fiverr-private/bianka_js/protobuf/bianka/enums/dynamic_matching/preview_drawer/user_mode';
import { TabName } from '@fiverr-private/bianka_js/protobuf/bianka/enums/dynamic_matching/preview_drawer/tab_name';
import { Status } from '@fiverr-private/bianka_js/protobuf/bianka/enums/dynamic_matching/brief/status';
import { Gql } from '@fiverr-private/dynamic_matching';
import type { SellerBriefResponse } from '../types/SellerBriefResponse';

const { DmBriefStatus } = Gql;

interface BriefPreviewParams {
    briefId: string;
    pageName: string;
    tabName?: string;
}

interface ClickedBriefParams {
    briefId: string;
    status?: SellerBriefResponse['status'];
    clickedOn: string;
}

const sharedImpData = {
    level: 1,
    visibleThroughScroll: false,
    positionInParent: 1,
    isLeaf: true,
    sizeInParent: 0,
};

const convertDmBriefStatusToStatus = (dmBriefStatus?: SellerBriefResponse['status']): Status => {
    switch (dmBriefStatus) {
        case DmBriefStatus.Active:
            return Status.ACTIVE;
        case DmBriefStatus.ClosedBuyerBlocked:
            return Status.CLOSED_BUYER_BLOCKED;
        case DmBriefStatus.ClosedByBuyer:
            return Status.CLOSED_BY_BUYER;
        case DmBriefStatus.ClosedFailedModeration:
            return Status.CLOSED_FAILED_MODERATION;
        case DmBriefStatus.ClosedStale:
            return Status.CLOSED_STALE;
        case DmBriefStatus.PendingModeration:
            return Status.PENDING_MODERATION;
        case DmBriefStatus.ClosedOrganizationRemoved:
            return Status.CLOSED_ORGANIZATION_REMOVED;
        default:
            return Status.UNSPECIFIED;
    }
};

const convertToTabName = (tabName?: string): TabName => {
    switch (tabName) {
        case 'brief':
            return TabName.BRIEF;
        case 'client_info':
            return TabName.CLIENT_INFO;
        default:
            return TabName.UNSPECIFIED;
    }
};

const reportBriefPreviewDrawerIsShown = (
    { briefId, pageName, tabName }: BriefPreviewParams,
    userMode: UserMode,
    role: 'seller' | 'buyer'
) => {
    bianka.reportActivity({
        type: Impression,
        event: {
            type: 'viewed_brief_drawer',
            page: {
                name: pageName,
                ctxId: getContext().pageCtxId,
            },
            component: {
                impId: getContext().pageCtxId,
                name: 'brief_drawer',
                ...sharedImpData,
            },
            organization: { id: getContext().organizationId || '' },
            user: { id: Number(getContext().userId) || 0 },
            [role]: { id: Number(getContext().userId) || 0 },
        },
        additionalData: {
            type: ViewedBriefDrawer,
            message: {
                briefId,
                tabName: convertToTabName(tabName),
                userMode,
            },
        },
    });
};

const reportClickedBriefDrawerCta = (
    { briefId, status, clickedOn }: ClickedBriefParams,
    userMode: UserMode,
    role: 'seller' | 'buyer'
) => {
    bianka.reportActivity({
        type: Click,
        event: {
            type: 'clicked_brief_drawer',
            clickType: ClickType.LEFT,
            element: {
                name: 'brief_drawer',
                type: ElementType.UNSPECIFIED,
                text: { value: clickedOn.substring(clickedOn.lastIndexOf('.') + 1) },
            },
            organization: { id: getContext().organizationId || '' },
            [role]: { id: Number(getContext().userId) || 0 },
        },
        additionalData: {
            type: ClickedBriefDrawer,
            message: {
                briefId,
                userMode,
                briefStatus: convertDmBriefStatusToStatus(status),
            },
        },
    });
};

export const sellerBriefPreviewDrawerIsShown = (params: BriefPreviewParams) =>
    reportBriefPreviewDrawerIsShown(params, UserMode.SELLER, 'seller');

export const sellerClickedBriefDrawerCta = (params: ClickedBriefParams) =>
    reportClickedBriefDrawerCta(params, UserMode.SELLER, 'seller');

export const buyerBriefPreviewDrawerIsShown = (params: BriefPreviewParams) =>
    reportBriefPreviewDrawerIsShown(params, UserMode.BUYER, 'buyer');

export const buyerClickedBriefDrawerCta = (params: ClickedBriefParams) =>
    reportClickedBriefDrawerCta(params, UserMode.BUYER, 'buyer');

interface ClickedElementBriefOverviewParams {
    briefId: string;
    elementName: string;
    elementType?: ElementType;
    briefStatus?: SellerBriefResponse['status'];
}

export const reportClickedElementBriefOverview = ({
    briefId,
    elementName,
    elementType = ElementType.BUTTON,
    briefStatus,
}: ClickedElementBriefOverviewParams) => {
    bianka.reportActivity<Click>({
        type: Click,
        event: {
            type: 'clicked_element_brief_overview',
            clickType: ClickType.LEFT,
            element: {
                name: elementName,
                type: elementType,
            },
            page: {
                name: 'brief_overview_drawer',
                ctxId: getContext().pageCtxId,
            },
            organization: { id: getContext().organizationId || '' },
            buyer: { id: Number(getContext().userId) || 0 },
        },
        additionalData: {
            type: ClickedBriefDrawer,
            message: {
                briefId,
                userMode: UserMode.BUYER,
                briefStatus: convertDmBriefStatusToStatus(briefStatus),
            },
        },
    });
};
